// Lightweight modal dialogs without adding new dependencies

// Non-blocking success toast -- bottom-right, auto-dismisses, no OK click required. Separate
// from showSuccess (the blocking modal below) on purpose: only a few call sites (shift
// assign/unassign) use this, everything else keeps the blocking modal unchanged.
let toastContainer = null;
function getToastContainer() {
  if (toastContainer && document.body.contains(toastContainer)) return toastContainer;
  toastContainer = document.createElement("div");
  toastContainer.style.position = "fixed";
  toastContainer.style.right = "20px";
  toastContainer.style.bottom = "20px";
  toastContainer.style.zIndex = "999999";
  toastContainer.style.display = "flex";
  toastContainer.style.flexDirection = "column";
  toastContainer.style.gap = "8px";
  document.body.appendChild(toastContainer);
  return toastContainer;
}

export function showToast(message, { duration = 3000 } = {}) {
  const container = getToastContainer();

  const toast = document.createElement("div");
  toast.setAttribute("role", "status");
  toast.style.display = "flex";
  toast.style.alignItems = "center";
  toast.style.gap = "8px";
  toast.style.padding = "10px 16px";
  toast.style.borderRadius = "8px";
  toast.style.background = "#00C445";
  toast.style.color = "#fff";
  toast.style.fontSize = "14px";
  toast.style.fontWeight = "500";
  toast.style.boxShadow = "0 6px 16px rgba(0,0,0,0.18)";
  toast.style.cursor = "pointer";
  toast.style.opacity = "0";
  toast.style.transform = "translateY(8px)";
  toast.style.transition = "opacity 0.2s ease, transform 0.2s ease";

  const icon = document.createElement("span");
  icon.textContent = "✓";
  icon.style.fontWeight = "700";

  const text = document.createElement("span");
  text.textContent = message;

  toast.appendChild(icon);
  toast.appendChild(text);
  container.appendChild(toast);

  let dismissed = false;
  function dismiss() {
    if (dismissed) return;
    dismissed = true;
    toast.style.opacity = "0";
    toast.style.transform = "translateY(8px)";
    setTimeout(() => {
      try { container.removeChild(toast); } catch (_) {}
    }, 200);
  }

  requestAnimationFrame(() => {
    toast.style.opacity = "1";
    toast.style.transform = "translateY(0)";
  });

  toast.addEventListener("click", dismiss);
  setTimeout(dismiss, duration);
}

function createBaseModal(message, title = "Message", onClose) {
  const overlay = document.createElement("div");
  overlay.style.position = "fixed";
  overlay.style.inset = "0";
  overlay.style.background = "rgba(0,0,0,0.45)";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.zIndex = "999999";

  const box = document.createElement("div");
  box.style.background = "#fff";
  box.style.borderRadius = "10px";
  box.style.width = "min(440px, 92vw)";
  box.style.boxShadow = "0 10px 30px rgba(0,0,0,0.2)";
  box.style.overflow = "hidden";
  box.setAttribute("role", "dialog");
  box.setAttribute("aria-modal", "true");

  const header = document.createElement("div");
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "space-between";
  header.style.padding = "12px 16px";
  header.style.borderBottom = "1px solid #eee";

  const titleEl = document.createElement("div");
  titleEl.style.fontWeight = "600";
  titleEl.textContent = title;

  const closeBtn = document.createElement("button");
  closeBtn.style.border = "0";
  closeBtn.style.background = "transparent";
  closeBtn.style.fontSize = "18px";
  closeBtn.style.cursor = "pointer";
  closeBtn.textContent = "×";

  header.appendChild(titleEl);
  header.appendChild(closeBtn);

  const body = document.createElement("div");
  body.style.padding = "16px";
  body.style.color = "#111827";
  body.textContent = message;

  const footer = document.createElement("div");
  footer.style.display = "flex";
  footer.style.justifyContent = "flex-end";
  footer.style.gap = "8px";
  footer.style.padding = "12px 16px";
  footer.style.borderTop = "1px solid #eee";

  const okBtn = document.createElement("button");
  okBtn.textContent = "OK";
  okBtn.style.padding = "8px 14px";
  okBtn.style.borderRadius = "8px";
  okBtn.style.border = "1px solid #ddd";
  okBtn.style.background = "#f7f7f7";
  okBtn.style.cursor = "pointer";

  footer.appendChild(okBtn);

  box.appendChild(header);
  box.appendChild(body);
  box.appendChild(footer);
  overlay.appendChild(box);

  function cleanup() {
    try { overlay?.parentNode?.removeChild(overlay); } catch (_) {}
    try { if (typeof onClose === 'function') onClose(); } catch (e) { console.error('dialog onClose error', e); }
  }

  okBtn.addEventListener("click", cleanup);
  closeBtn.addEventListener("click", cleanup);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) cleanup();
  });

  document.body.appendChild(overlay);
}

export function showSuccess(message, titleOrCallback = "Success") {
  if (typeof titleOrCallback === 'function') {
    createBaseModal(message, "Success", titleOrCallback);
  } else {
    createBaseModal(message, titleOrCallback);
  }
}

export function showError(message, titleOrCallback = "Error") {
  if (typeof titleOrCallback === 'function') {
    createBaseModal(message, "Error", titleOrCallback);
  } else {
    createBaseModal(message, titleOrCallback);
  }
}

export function showConfirm(message, title = "Confirm") {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.background = "rgba(0,0,0,0.45)";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.zIndex = "999999";

    const box = document.createElement("div");
    box.style.background = "#fff";
    box.style.borderRadius = "10px";
    box.style.width = "min(440px, 92vw)";
    box.style.boxShadow = "0 10px 30px rgba(0,0,0,0.2)";
    box.style.overflow = "hidden";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");

    const header = document.createElement("div");
    header.style.display = "flex";
    header.style.alignItems = "center";
    header.style.justifyContent = "space-between";
    header.style.padding = "12px 16px";
    header.style.borderBottom = "1px solid #eee";

    const titleEl = document.createElement("div");
    titleEl.style.fontWeight = "600";
    titleEl.textContent = title;

    const closeBtn = document.createElement("button");
    closeBtn.style.border = "0";
    closeBtn.style.background = "transparent";
    closeBtn.style.fontSize = "18px";
    closeBtn.style.cursor = "pointer";
    closeBtn.textContent = "×";

    header.appendChild(titleEl);
    header.appendChild(closeBtn);

    const body = document.createElement("div");
    body.style.padding = "16px";
    body.style.color = "#111827";
    body.textContent = message;

    const footer = document.createElement("div");
    footer.style.display = "flex";
    footer.style.justifyContent = "flex-end";
    footer.style.gap = "8px";
    footer.style.padding = "12px 16px";
    footer.style.borderTop = "1px solid #eee";

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.padding = "8px 14px";
    cancelBtn.style.borderRadius = "8px";
    cancelBtn.style.border = "1px solid #ddd";
    cancelBtn.style.background = "#f7f7f7";
    cancelBtn.style.cursor = "pointer";

    const confirmBtn = document.createElement("button");
    confirmBtn.textContent = "Confirm";
    confirmBtn.style.padding = "8px 14px";
    confirmBtn.style.borderRadius = "8px";
    confirmBtn.style.border = "0";
    confirmBtn.style.background = "#dc1f00";
    confirmBtn.style.color = "white";
    confirmBtn.style.cursor = "pointer";

    footer.appendChild(cancelBtn);
    footer.appendChild(confirmBtn);

    box.appendChild(header);
    box.appendChild(body);
    box.appendChild(footer);
    overlay.appendChild(box);

    function cleanup(val) {
      overlay?.parentNode?.removeChild(overlay);
      resolve(!!val);
    }

    cancelBtn.addEventListener("click", () => cleanup(false));
    closeBtn.addEventListener("click", () => cleanup(false));
    confirmBtn.addEventListener("click", () => cleanup(true));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) cleanup(false);
    });

    document.body.appendChild(overlay);
  });
}

// Prompt dialog that returns the entered text or null if cancelled
export function showPrompt(message, title = "Input", placeholder = '', submitLabel = 'Submit') {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.background = "rgba(0,0,0,0.45)";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.zIndex = "999999";

    const box = document.createElement("div");
    box.style.background = "#fff";
    box.style.borderRadius = "10px";
    box.style.width = "min(600px, 96vw)";
    box.style.boxShadow = "0 10px 30px rgba(0,0,0,0.2)";
    box.style.overflow = "hidden";
    box.setAttribute("role", "dialog");
    box.setAttribute("aria-modal", "true");

    const header = document.createElement("div");
    header.style.display = "flex";
    header.style.alignItems = "center";
    header.style.justifyContent = "space-between";
    header.style.padding = "12px 16px";
    header.style.borderBottom = "1px solid #eee";

    const titleEl = document.createElement("div");
    titleEl.style.fontWeight = "600";
    titleEl.textContent = title;

    const closeBtn = document.createElement("button");
    closeBtn.style.border = "0";
    closeBtn.style.background = "transparent";
    closeBtn.style.fontSize = "18px";
    closeBtn.style.cursor = "pointer";
    closeBtn.textContent = "×";

    header.appendChild(titleEl);
    header.appendChild(closeBtn);

    const body = document.createElement("div");
    body.style.padding = "12px 16px";
    body.style.color = "#111827";

    const msg = document.createElement('div');
    msg.style.marginBottom = '8px';
    msg.textContent = message;

    const textarea = document.createElement('textarea');
    textarea.placeholder = placeholder;
    textarea.style.width = '100%';
    textarea.style.minHeight = '120px';
    textarea.style.padding = '8px';
    textarea.style.border = '1px solid #ddd';
    textarea.style.borderRadius = '6px';
    textarea.style.fontSize = '14px';

    body.appendChild(msg);
    body.appendChild(textarea);

    const footer = document.createElement("div");
    footer.style.display = "flex";
    footer.style.justifyContent = "flex-end";
    footer.style.gap = "8px";
    footer.style.padding = "12px 16px";
    footer.style.borderTop = "1px solid #eee";

    const cancelBtn = document.createElement("button");
    cancelBtn.textContent = "Cancel";
    cancelBtn.style.padding = "8px 14px";
    cancelBtn.style.borderRadius = "8px";
    cancelBtn.style.border = "1px solid #ddd";
    cancelBtn.style.background = "#f7f7f7";
    cancelBtn.style.cursor = "pointer";

    const submitBtn = document.createElement("button");
    submitBtn.textContent = submitLabel;
    submitBtn.style.padding = "8px 14px";
    submitBtn.style.borderRadius = "8px";
    submitBtn.style.border = "0";
    submitBtn.style.background = "#dc1f00";
    submitBtn.style.color = "white";
    submitBtn.style.cursor = "pointer";

    footer.appendChild(cancelBtn);
    footer.appendChild(submitBtn);

    box.appendChild(header);
    box.appendChild(body);
    box.appendChild(footer);
    overlay.appendChild(box);

    function cleanup(returnValue) {
      try { overlay?.parentNode?.removeChild(overlay); } catch (_) {}
      resolve(returnValue);
    }

    cancelBtn.addEventListener('click', () => cleanup(null));
    closeBtn.addEventListener('click', () => cleanup(null));
    submitBtn.addEventListener('click', () => cleanup(textarea.value.trim() || null));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(null); });

    document.body.appendChild(overlay);
    textarea.focus();
  });
}


